import { FC, ReactNode } from "react";
import { HelpCircle } from "lucide-react";

import { PunchupTooltip } from "@punchup/ui-common/punchup/primitives/feedback/PunchupTooltip";
import {
  helpTriggerLabel,
  resolveTooltipHelp,
  type PunchupTooltipHelp,
} from "@punchup/ui-common/punchup/primitives/feedback/PunchupTooltip/help";
import styles from "./PunchupFieldLabel.module.css";

/** The forms name for `PunchupTooltipHelp`. */
export type PunchupFieldHelp = PunchupTooltipHelp;

export interface PunchupFieldHelpIconProps {
  /** The help content revealed on hover/focus. */
  help: PunchupFieldHelp;
  /** Accessible name for the help trigger. */
  ariaLabel: string;
}

/**
 * PunchupFieldHelpIcon
 *
 * The help affordance on its own — a muted 12px `HelpCircle` that opens a `PunchupTooltip` on hover/focus. For a
 * control whose label must NOT route through `PunchupFieldLabel` (the toggles, whose label carries no bottom
 * margin); render it as a SIBLING of the `<label>`, never inside it (which would forward clicks to the control).
 * Wired to `PunchupTooltip` directly — never the card-corner `PunchupHelpButton`. The tooltip's `triggerLabel`
 * makes its wrapper the focusable, screen-reader-named control, so the icon itself stays bare and `aria-hidden`.
 */
export const PunchupFieldHelpIcon: FC<PunchupFieldHelpIconProps> = ({
  help,
  ariaLabel,
}) => {
  return (
    <PunchupTooltip {...resolveTooltipHelp(help)} triggerLabel={ariaLabel}>
      {/* 12px to match the analytics KPI help icon (PunchupHelpButton) — same muted colour + size so the
          help affordance reads identically everywhere. The lucide stroke inherits the muted token via the
          wrapper's currentColor; the icon itself is hidden from the a11y tree (the tooltip wrapper carries
          the accessible name). */}
      <span className={styles.help} aria-hidden>
        <HelpCircle size={12} />
      </span>
    </PunchupTooltip>
  );
};

export interface PunchupFieldLabelProps {
  /** Label text / content. */
  label: ReactNode;
  /** Associates a `<label>` with its control by id. Ignored when `as="span"`. */
  htmlFor?: string;
  /** Shows the required marker via the `punchupFormLabel` `data-required` hook. */
  required?: boolean;
  /**
   * Optional "explain this field" help. Renders a muted 12px help icon beside the label that opens a
   * `PunchupTooltip` on hover/focus. Use for a field's STATIC purpose — not for contextual state
   * (`PunchupNumericInput.hint`), toggle `info`, or radio-card `description`, which own those. A string is
   * one paragraph; `{ mainText, secondaryText }` adds a worked example below it.
   */
  help?: PunchupFieldHelp;
  /** `"label"` (default) renders `<label htmlFor>`; `"span"` for group labels that have no single control. */
  as?: "label" | "span";
  /** Accessible name for the help trigger. Defaults from `label` when it is a string. */
  helpAriaLabel?: string;
}

/**
 * PunchupFieldLabel
 *
 * The single source for a Punchup form-field label. Owns the canonical `punchupFormLabel` markup + the
 * `data-required` asterisk hook (the global rule lives in `packages/styles/punchup-typography.css`), and adds
 * an optional inline help affordance — {@link PunchupFieldHelpIcon} beside the label. The tooltip's
 * `triggerLabel` makes the trigger keyboard-focusable + screen-reader-named, so this component does not re-roll
 * `tabIndex` / `role` / `aria-label` / a focus ring (the tixologist twin did; the tooltip owns that contract here).
 *
 * Pure prop-forwarder (no hooks / browser APIs of its own) → no `"use client"`, matching the buttons /
 * display precedent; the interactive directive belongs on the consuming field's client boundary. With no
 * `help` it renders just the bare `punchupFormLabel` label.
 */
export const PunchupFieldLabel: FC<PunchupFieldLabelProps> = ({
  label,
  htmlFor,
  required,
  help,
  as = "label",
  helpAriaLabel,
}) => {
  const LabelTag = as;
  const labelNode = (
    <LabelTag
      className="punchupFormLabel"
      htmlFor={as === "label" ? htmlFor : undefined}
      data-required={required || undefined}
    >
      {label}
    </LabelTag>
  );

  if (!help) return labelNode;

  const ariaLabel = helpAriaLabel ?? helpTriggerLabel(label);

  return (
    <div className={styles.labelRow}>
      {labelNode}
      <PunchupFieldHelpIcon help={help} ariaLabel={ariaLabel} />
    </div>
  );
};
